/// <reference types="datatables.net" />
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit, QueryList, ViewChildren, Input, TemplateRef, ViewChild } from '@angular/core';
import { SortableDirective } from 'src/app/directives/sortable.directive';
import { TableProviderService } from 'src/app/services/table-provider.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ExcelService } from 'src/app/services/excel.service';
import { environment } from 'src/environments/environment';
import { DataTableDirective } from 'angular-datatables';

export class ButtonClass {
  texto: string;
  css: string = '';
  accion: () => void;
}


@Component({
  selector: 'app-admin-table',
  templateUrl: './admin-table.component.html',
  styleUrls: ['./admin-table.component.css']
})
export class AdminTableComponent<T> implements OnInit, OnDestroy {

  @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective<T>>;

  @Input("mapHeader") mapHeader: any;
  @Input("queryset") queryset: any;
  @Input("exportFile") exportFile: any;
  @Input() extraCols?: TemplateRef<any>;
  @ViewChild(DataTableDirective, { static: false })
  private readonly datatableElement!: DataTableDirective;
  dtOptions: any = {};
  @Input() botones: ButtonClass[] = [];
  constructor(
    private readonly http: HttpClient,
    private readonly spinner: NgxSpinnerService,
    private readonly excelService: ExcelService,
    public service: TableProviderService<T>) {
  }

  ngOnInit(): void {
    const columns: Record<string, unknown>[] = [];
    for (const key in this.mapHeader) {
      const value = this.mapHeader[key];
      const isDataFunction = typeof value['data'] === 'function';
      const columnDef: Record<string, unknown> = {
        visible: value['visible'] ?? true,
        orderable: value['sort'] ?? true,
        className: key === 'actions' ? 'action-column' : 'text-table-left'
      };
      if (isDataFunction) {
        columnDef['data'] = null;
        columnDef['render'] = (_data: unknown, _type: string, row: Record<string, unknown>) =>
          (value['data'] as (row: Record<string, unknown>) => string)(row);
      } else {
        columnDef['data'] = value['data'] ?? key;
      }
      columns.push(columnDef);
    }
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 10,
      serverSide: true,
      columns: columns,
      order: [[0, 'desc']],
      scrollX: true,
      language: {
        lengthMenu: "Mostrar _MENU_ registros por página",
        zeroRecords: "No se encontraron resultados",
        info: "Mostrando página _PAGE_ de _PAGES_",
        infoEmpty: "No hay registros disponibles",
        infoFiltered: "(filtrado de _MAX_ registros totales)",
        search: "Buscar:",
        paginate: {
          first: "Primero",
          last: "Último",
          next: "Siguiente",
          previous: "Anterior"
        }
      },
      rowCallback: (row: Node, data: any[] | Object, index: number) => {
        const self = this;
        $('.btn-edit', row).off('click');
        $('.btn-edit', row).on('click', () => {
          let func = self.mapHeader['actions']['edit'] ?? null;
          if (func !== null)
            func(data);
        });
        $('.btn-delete', row).off('click');
        $('.btn-delete', row).on('click', () => {
          let func = self.mapHeader['actions']['delete'] ?? null;
          if (func !== null)
            func(data);
        });

        // Manejador general para todos los botones con la clase 'btn-action'
        $('.btn-action', row).off('click');
        $('.btn-action', row).on('click', (event: JQuery.TriggeredEvent) => {
          let action = $(event.currentTarget as HTMLElement).data('action');  // Lee el valor de 'data-action'
          let func = self.mapHeader['actions'][action] ?? null;

          if (func !== null) {
            func(data);  // Llama a la función correspondiente según 'data-action'
          }
        });

        return row;
      },
      ajax: (dataTablesParameters: Record<string, unknown>, callback: (data: unknown) => void) => {
        const params = this.buildDataTableHttpParams(dataTablesParameters);
        this.http.get<unknown>(`${environment.apiUrl}${this.queryset}`, { params }).subscribe({
          next: (response) => {
            callback(response);
          },
          error: () => {
            callback({
              data: [],
              draw: Number(dataTablesParameters['draw'] ?? 0),
              recordsFiltered: 0,
              recordsTotal: 0,
            });
          }
        });
      },
      initComplete: function () {
        $('.dt-type-numeric, .dt-type-date').css('text-align', 'left');
      }
    };
  }

  ngOnDestroy(): void {
    this.datatableElement?.dtInstance
      ?.then((dtInstance) => {
        dtInstance.destroy(true);
      })
      .catch((error) => {
      });
  }

  onSort({ column, direction }: any) {
    this.headers.forEach(header => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });

    this.service.sortColumn = column;
    this.service.sortDirection = direction;
  }

  reload() {
    this.datatableElement?.dtInstance.then((dtInstance) => dtInstance.draw());
  }

  compare(a: any, b: any) {
    return 0;
  }

  private buildDataTableHttpParams(parameters: Record<string, unknown>): HttpParams {
    let params = new HttpParams();

    const appendValue = (key: string, value: unknown): void => {
      if (value == null) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          appendValue(`${key}[${index}]`, item);
        });
        return;
      }

      if (typeof value === 'object') {
        Object.entries(value as Record<string, unknown>).forEach(([nestedKey, nestedValue]) => {
          appendValue(`${key}[${nestedKey}]`, nestedValue);
        });
        return;
      }

      params = params.append(key, String(value));
    };

    Object.entries(parameters).forEach(([key, value]) => {
      appendValue(key, value);
    });

    return params;
  }

  exportData() {
    // Obtener el estado actual de la tabla DataTables
    this.datatableElement?.dtInstance.then((dtInstance: any) => {
      const settings = dtInstance.settings()[0];
      const api = dtInstance;
      
      // Obtener parámetros actuales de la tabla
      const searchValue = api.search() || '';
      const order = api.order();
      const orderColumn = order.length > 0 ? order[0][0] : 0;
      const orderDir = order.length > 0 ? order[0][1] : 'desc';
      
      // Construir la URL con los parámetros actuales de la tabla
      const params = new URLSearchParams();
      params.set('draw', '1');
      params.set('length', '-1'); // -1 significa todos los registros filtrados
      params.set('start', '0');
      params.set(`order[0][column]`, orderColumn.toString());
      params.set(`order[0][dir]`, orderDir);
      params.set('search[value]', searchValue);
      params.set('search[regex]', 'false');
      
      // Agregar parámetros de columnas (necesarios para DataTables)
      const columns = settings.aoColumns;
      columns.forEach((col: any, index: number) => {
        params.set(`columns[${index}][data]`, 'function');
        params.set(`columns[${index}][name]`, '');
        params.set(`columns[${index}][searchable]`, 'true');
        params.set(`columns[${index}][orderable]`, 'true');
        params.set(`columns[${index}][search][value]`, '');
        params.set(`columns[${index}][search][regex]`, 'false');
      });
      
      const queryString = params.toString();
      this.excelService.exportToFile(this.exportFile, `${this.queryset}?${queryString}`, this.mapHeader);
    });
  }

  getDisplay(item: any): string {
    return item?.display;
  }
}
